import React, { useEffect, useMemo } from "react";
import { Box, CircularProgress } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useChildContext } from "../../contexts/ChildContext";
import { useRole } from "../../contexts/RoleContext";
import useChildName from "../../hooks/useChildName";
import DailyCareReport from "../../components/Reports/DailyCareReport";
import { ACTIVE_CHILD_STORAGE_KEY } from "../../components/Dashboard/shared/DashboardViewContext";
import ChildShellBottomNav from "../../components/Dashboard/shared/ChildShellBottomNav";
import { getChildAccentTheme } from "../../components/Dashboard/shared/childAccentTheme";

const TherapyPrepPage = () => {
  const navigate = useNavigate();
  const { childId } = useParams();
  const { currentChildId, setCurrentChildId } = useChildContext();
  const { childrenWithAccess, loading: roleLoading } = useRole();
  const storedChildId = useMemo(() => {
    if (typeof window === "undefined") {
      return "";
    }

    return window.localStorage.getItem(ACTIVE_CHILD_STORAGE_KEY) || "";
  }, []);
  const effectiveChildId = childId
    || currentChildId
    || storedChildId
    || childrenWithAccess?.[0]?.id
    || "";
  const child = childrenWithAccess?.find((item) => item.id === effectiveChildId) || null;
  const childAccent = useMemo(
    () => getChildAccentTheme(child || {}, { children: childrenWithAccess }),
    [child, childrenWithAccess]
  );
  const { childName, loading: childNameLoading, error } = useChildName(effectiveChildId);

  useEffect(() => {
    if (effectiveChildId && effectiveChildId !== currentChildId) {
      setCurrentChildId(effectiveChildId);
    }
  }, [effectiveChildId, currentChildId, setCurrentChildId]);

  if (roleLoading && !child) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roleLoading && !child) {
    return <Navigate to="/dashboard" replace />;
  }

  if (childNameLoading) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Navigate to="/dashboard" replace />;
  }

  const navigateToDashboard = (target = "tools") => {
    navigate("/dashboard", {
      state: {
        selectedChildId: effectiveChildId || null,
        childShellTarget: target,
      },
    });
  };

  const handleChildShellNavSelect = (itemId) => {
    if (itemId === "home") {
      navigate("/home", { state: { selectedChildId: effectiveChildId || null } });
      return;
    }

    if (itemId === "tools") {
      navigateToDashboard("tools");
      return;
    }

    if (itemId === "profile") {
      navigate("/care-profile", {
        state: { childId: effectiveChildId || null },
      });
      return;
    }

    navigateToDashboard("today");
  };

  return (
    <>
      <DailyCareReport
        child={child}
        childId={effectiveChildId}
        childName={childName || child?.name || "Visit Prep"}
        childAccent={childAccent}
        onBack={() => navigateToDashboard("tools")}
      />
      <ChildShellBottomNav
        activeItem="tools"
        accent={childAccent}
        onSelect={handleChildShellNavSelect}
      />
    </>
  );
};

export default TherapyPrepPage;
